import { Ionicons } from '@expo/vector-icons';
import * as Haptics from 'expo-haptics';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

export interface Action {
  id: number;
  /** i18n key of the action name (e.g. 'actions.grta.name'), rendered via t(). */
  nameKey: string;
  icon: any;
  /** i18n key of the action description. */
  descriptionKey: string;
  category: string;
  points: number;
  infoUrl?: string;
  /** i18n key of the "learn more" link label. */
  infoLabelKey?: string;
}

interface ActionCardProps {
  action: Action;
  isSelected: boolean;
  onPress: (id: number) => void;
  onInfoPress: (action: Action) => void;
}

export default function ActionCard({
  action,
  isSelected,
  onPress,
  onInfoPress,
}: ActionCardProps) {
  const { t } = useTranslation();
  const handlePress = async () => {
    try {
      if (isSelected) {
        await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      } else {
        await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      }
    } catch (error) {
      // Haptics not available
    }
    onPress(action.id);
  };

  return (
    <TouchableOpacity style={styles.actionCard} onPress={handlePress}>
      {isSelected && (
        <LinearGradient
          colors={['rgba(131, 191, 79, 0.65)', 'rgba(131, 191, 79, 0.5)']}
          style={styles.actionCardGradient}
        />
      )}
      <LinearGradient
        colors={['#FCC189', '#CD7728']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.pointsBadge}
      >
        <Text style={styles.pointsText}>+{action.points || 10}</Text>
      </LinearGradient>
      <TouchableOpacity
        style={styles.infoBadge}
        onPress={(e) => {
          e.stopPropagation();
          onInfoPress(action);
        }}
      >
        <Ionicons name="information" size={14} color="#666" />
      </TouchableOpacity>
      <View style={styles.actionIconContainer}>
        {React.createElement(action.icon, { width: 32, height: 32 })}
      </View>
      <Text
        style={[styles.actionName, isSelected && styles.actionNameSelected]}
        numberOfLines={2}
        adjustsFontSizeToFit
        minimumFontScale={0.7}
        ellipsizeMode="tail"
      >
        {t(action.nameKey)}
      </Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  actionCard: {
    width: '31%',
    aspectRatio: 1,
    backgroundColor: '#FFF',
    borderRadius: 16,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  actionCardGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  pointsBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  pointsText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: '#FFF',
  },
  infoBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#f1f1f1ff',
  },
  actionIconContainer: {
    width: 40,
    height: 40,
    marginBottom: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: '#000',
    textAlign: 'center',
  },
  actionNameSelected: {
    color: '#FFF',
  },
});
